"use client";
import { useEffect, useRef } from "react";
import { Route, GitMerge, UsersRound, AlertTriangle } from "lucide-react";

export default function IndustriesSection() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll(".scroll-reveal");
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) {
      targets.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-revealed");
          else entry.target.classList.remove("is-revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }
    );
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section ref={rootRef} className="section-pad bg-purple-glow relative overflow-hidden">
      <div className="container-c relative">
        <div className="reveal-grid grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="scroll-reveal lg:col-span-5 lg:order-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1.5 text-xs font-medium text-ink shadow-card">
              <span className="h-2 w-2 rounded-full bg-purple"></span>
              <span className="text-purple font-semibold">Director network mapping</span>
            </div>
            <h2 className="mt-5 font-display text-[34px] leading-[1.05] tracking-tightest text-balance text-ink sm:text-[42px] md:text-[48px]">
              See what no other tool will tell you.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-muted text-pretty">
              The customer is clean. The co-director might not be. Hidden director networks are where most enterprise fraud actually lives, and no global sanctions tool surfaces them. RiskIntel does.
            </p>

            <ul className="mt-7 space-y-5">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-purple-soft text-purple">
                  <Route className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold text-ink">A risk path on every match card</p>
                  <p className="text-[14px] leading-relaxed text-muted">
                    4-hop network BFS attaches a <span className="font-mono text-[13px] text-ink">risk_path</span> and <span className="font-mono text-[13px] text-ink">hops_to_risk</span> to every result, so analysts know exactly how a customer connects to sanctioned or PEP entities.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-purple-soft text-purple">
                  <GitMerge className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold text-ink">MCA + OpenCorporates cross-reference</p>
                  <p className="text-[14px] leading-relaxed text-muted">
                    225K+ MCA-debarred directors indexed, plus query-time OpenCorporates enrichment with a 30-day cache. Source-provenance pills on every edge (MCA / OC / MCA+OC).
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-purple-soft text-purple">
                  <UsersRound className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold text-ink">PEP proximity, family-aware</p>
                  <p className="text-[14px] leading-relaxed text-muted">
                    Severity scores bump when a family relationship is detected. Strength-proportional graph edges, with dashed soft-merge edges for &quot;Possible Same Person&quot; cross-source matches.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="scroll-reveal lg:col-span-7 lg:order-1">
            <div className="rounded-3xl border-2 border-purple/30 bg-white p-2 shadow-card-hover">
              <div className="rounded-2xl border border-line bg-white">
                <div className="flex items-center justify-between border-b border-line px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#E5484D]/70"></span>
                    <span className="h-2.5 w-2.5 rounded-full bg-[#F4A11E]/70"></span>
                    <span className="h-2.5 w-2.5 rounded-full bg-[#2EBD7E]/70"></span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] font-medium text-muted">
                    <svg className="h-3 w-3 text-purple" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="6" cy="3" r="2" />
                      <circle cx="6" cy="21" r="2" />
                      <circle cx="18" cy="12" r="2" />
                      <path d="M6 5v6a3 3 0 0 0 3 3h6" />
                      <path d="M6 19v-6" />
                    </svg>
                    Network · CIN <span className="font-mono">U45201RJ2008PTC026807</span>
                  </div>
                  <div className="w-12"></div>
                </div>

                <div className="relative p-4" style={{ background: "#F4F6FA" }}>
                  <svg viewBox="0 0 600 440" className="block h-auto w-full" aria-hidden="true">
                    <defs>
                      <radialGradient id="dirGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#6F5BFB" stopOpacity="0.28" />
                        <stop offset="100%" stopColor="#6F5BFB" stopOpacity="0" />
                      </radialGradient>
                      <radialGradient id="riskGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#E5484D" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#E5484D" stopOpacity="0" />
                      </radialGradient>
                      <radialGradient id="pepGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#F4A11E" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#F4A11E" stopOpacity="0" />
                      </radialGradient>
                      <marker id="arrowRisk" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#E5484D" />
                      </marker>
                      <pattern id="graphGrid" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
                        <circle cx="1" cy="1" r="1" fill="rgba(91, 100, 120, 0.32)" />
                      </pattern>
                      <radialGradient id="gridFade" cx="50%" cy="50%" r="65%">
                        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
                        <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.2" />
                      </radialGradient>
                      <mask id="gridMask">
                        <rect x="0" y="0" width="600" height="440" fill="url(#gridFade)" />
                      </mask>
                    </defs>

                    <rect x="0" y="0" width="600" height="440" fill="url(#graphGrid)" mask="url(#gridMask)" />

                    <g stroke="#C9C2EF" strokeWidth="1.5" strokeLinecap="round">
                      <line x1="300" y1="220" x2="150" y2="105" />
                      <line x1="300" y1="220" x2="295" y2="65" />
                      <line x1="300" y1="220" x2="450" y2="105" />
                      <line x1="300" y1="220" x2="510" y2="220" />
                      <line x1="300" y1="220" x2="450" y2="335" />
                      <line x1="300" y1="220" x2="295" y2="375" />
                      <line x1="300" y1="220" x2="150" y2="335" />
                    </g>

                    <g stroke="#B8B0E6" strokeWidth="1.2" strokeLinecap="round" strokeOpacity="0.85">
                      <line x1="150" y1="105" x2="295" y2="65" />
                      <line x1="295" y1="65" x2="450" y2="105" />
                      <line x1="510" y1="220" x2="450" y2="335" />
                      <line x1="450" y1="335" x2="295" y2="375" />
                      <line x1="295" y1="375" x2="150" y2="335" />
                    </g>

                    <line x1="450" y1="105" x2="548" y2="58" stroke="#E5484D" strokeWidth="2.2" strokeLinecap="round" markerEnd="url(#arrowRisk)" />
                    <line x1="450" y1="335" x2="548" y2="380" stroke="#F4A11E" strokeWidth="2" strokeLinecap="round" markerEnd="url(#arrowRisk)" strokeOpacity="0.85" />
                    <line x1="150" y1="105" x2="150" y2="335" stroke="#9C8FED" strokeWidth="1.5" strokeDasharray="5 4" strokeLinecap="round" />

                    <circle cx="300" cy="220" r="56" fill="url(#dirGlow)" />
                    <circle cx="300" cy="220" r="36" fill="#1E5BFF" />
                    <text x="300" y="226" textAnchor="middle" fill="#FFFFFF" fontSize="16" fontWeight="700" fontFamily="Inter, sans-serif">CIN</text>
                    <text x="300" y="285" textAnchor="middle" fill="#0F172A" fontSize="11" fontWeight="600" fontFamily="Inter, sans-serif">7 directors</text>

                    <g>
                      <circle cx="150" cy="105" r="22" fill="url(#dirGlow)" />
                      <circle cx="150" cy="105" r="14" fill="#6F5BFB" />
                      <text x="150" y="108" textAnchor="middle" fill="#FFFFFF" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">D1</text>

                      <circle cx="295" cy="65" r="22" fill="url(#dirGlow)" />
                      <circle cx="295" cy="65" r="14" fill="#6F5BFB" />
                      <text x="295" y="68" textAnchor="middle" fill="#FFFFFF" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">D2</text>

                      <circle cx="450" cy="105" r="28" fill="url(#riskGlow)" />
                      <circle cx="450" cy="105" r="16" fill="#FFFFFF" stroke="#E5484D" strokeWidth="2.5" />
                      <text x="450" y="108" textAnchor="middle" fill="#E5484D" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">D3</text>

                      <circle cx="510" cy="220" r="22" fill="url(#dirGlow)" />
                      <circle cx="510" cy="220" r="14" fill="#6F5BFB" />
                      <text x="510" y="223" textAnchor="middle" fill="#FFFFFF" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">D4</text>

                      <circle cx="450" cy="335" r="26" fill="url(#pepGlow)" />
                      <circle cx="450" cy="335" r="14" fill="#FFFFFF" stroke="#F4A11E" strokeWidth="2.5" />
                      <text x="450" y="338" textAnchor="middle" fill="#B6700F" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">D5</text>

                      <circle cx="295" cy="375" r="22" fill="url(#dirGlow)" />
                      <circle cx="295" cy="375" r="14" fill="#6F5BFB" />
                      <text x="295" y="378" textAnchor="middle" fill="#FFFFFF" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">D6</text>

                      <circle cx="150" cy="335" r="22" fill="url(#dirGlow)" />
                      <circle cx="150" cy="335" r="14" fill="#6F5BFB" />
                      <text x="150" y="338" textAnchor="middle" fill="#FFFFFF" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">D7</text>
                    </g>

                    <circle cx="548" cy="58" r="30" fill="url(#riskGlow)" />
                    <circle cx="548" cy="58" r="18" fill="#E5484D" />
                    <text x="548" y="62" textAnchor="middle" fill="#FFFFFF" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">UAPA</text>
                    <text x="548" y="102" textAnchor="middle" fill="#E5484D" fontSize="11" fontWeight="600" fontFamily="Inter, sans-serif">Sanctioned</text>

                    <circle cx="548" cy="380" r="26" fill="url(#pepGlow)" />
                    <circle cx="548" cy="380" r="16" fill="#F4A11E" />
                    <text x="548" y="384" textAnchor="middle" fill="#FFFFFF" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">PEP</text>
                    <text x="548" y="418" textAnchor="middle" fill="#B6700F" fontSize="11" fontWeight="600" fontFamily="Inter, sans-serif">Domestic PEP</text>

                    <text x="80" y="218" textAnchor="middle" fill="#9C8FED" fontSize="10" fontFamily="Inter, sans-serif">Possible</text>
                    <text x="80" y="232" textAnchor="middle" fill="#9C8FED" fontSize="10" fontFamily="Inter, sans-serif">same person</text>
                  </svg>

                  <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-line bg-surface px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBE5E6] px-2.5 py-1 text-[11px] font-semibold text-danger">
                      <AlertTriangle className="h-3 w-3" /> Risk path detected
                    </span>
                    <span className="text-[13px] text-ink">
                      <span className="font-mono text-[12px]">CIN → D3 → UAPA-listed entity</span>
                    </span>
                    <span className="ml-auto text-[11px] font-medium text-muted">
                      <span className="font-mono">hops_to_risk: 2</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-3 text-center text-[12px] text-muted">
              Illustrative network graph. The live product visualisation lives on the RiskIntel platform.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
